/**
 * /alerts — the in-app notification feed as a full page (Crew UX.3).
 *
 * Same data as the header bell, but a thumb-friendly list for crew on a phone:
 * tap a row to mark it read and jump to the linked task/project.
 */
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Loader2 } from 'lucide-react';
import { formatRelative } from '@/lib/format';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import {
  useMarkAllRead,
  useMarkNotificationRead,
  useMyNotifications,
  type NotificationStoredPayload,
} from '@/api/my-notifications';

export function Alerts() {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useMyNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllRead();

  const unread = data?.unread_count ?? 0;
  const items = data?.data ?? [];

  function open(id: string, actionUrl: string | null) {
    markRead.mutate(id);
    if (!actionUrl) return;
    try {
      const url = new URL(actionUrl, window.location.origin);
      if (url.origin === window.location.origin) {
        navigate(url.pathname + url.search + url.hash);
        return;
      }
    } catch {
      // fall through to a hard navigation
    }
    window.location.href = actionUrl;
  }

  return (
    <div className="min-h-full bg-fill-soft pb-20 lg:pb-8">
      <div className="max-w-2xl mx-auto px-4 pt-4 space-y-4">
        <header className="flex items-end justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-faint flex items-center gap-1">
              <Bell className="w-3 h-3" /> Alerts
            </div>
            <h1 className="text-2xl font-semibold mt-0.5">Notifications</h1>
            <p className="text-sm text-ink-dim">
              {unread > 0 ? `${unread} unread` : 'All caught up'}
            </p>
          </div>
          {unread > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => markAll.mutate()}
              className="shrink-0"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Mark all read
            </Button>
          )}
        </header>

        {isLoading ? (
          <div className="py-12 grid place-items-center">
            <Loader2 className="w-5 h-5 animate-spin text-ink-faint" />
          </div>
        ) : error ? (
          // Distinct from empty: "no notifications" while the feed is down is
          // a false all-clear.
          <div className="py-16 text-center text-sm" role="alert">
            <Bell className="w-6 h-6 mx-auto mb-2 opacity-40 text-status-blocked-fg" />
            <div className="text-status-blocked-fg">
              Couldn&apos;t load notifications ({error.message}).
            </div>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-sm text-ink-faint">
            <Bell className="w-6 h-6 mx-auto mb-2 opacity-40" />
            No notifications yet.
          </div>
        ) : (
          <ul className="space-y-2">
            {items.map((n) => {
              const p = n.payload as unknown as NotificationStoredPayload;
              const isUnread = n.status === 'sent';
              return (
                <li key={n.id}>
                  <button
                    onClick={() => open(n.id, p.action_url)}
                    className={cn(
                      'w-full text-left rounded-lg border p-3 transition-colors hover:border-ink-faint',
                      isUnread
                        ? 'border-line bg-bg'
                        : 'border-line bg-fill-soft/40',
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className={cn(
                          'w-1.5 h-1.5 rounded-full mt-1.5 shrink-0',
                          isUnread ? 'bg-sky-600' : 'bg-transparent',
                        )}
                      />
                      <div className="flex-1 min-w-0">
                        <div
                          className={cn(
                            'text-sm',
                            isUnread ? 'font-semibold' : 'text-ink-dim',
                          )}
                        >
                          {p.subject ?? p.event}
                        </div>
                        {p.short && (
                          <div className="text-xs text-ink-dim mt-0.5">
                            {p.short}
                          </div>
                        )}
                        <div className="text-[10px] text-ink-faint mt-1">
                          {formatRelative(n.queued_at)}
                        </div>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
